<template>
  <div class="d-flex flex-column fill-height">
    <!-- Chat Content -->
    <v-card-text ref="chatContainer" class="flex-grow-1 pa-4 ai-chat-content d-flex flex-column">
      <!-- Chat History -->
      <div v-for="message in chatMessages" :key="message.id" class="mb-4">
        <!-- User Message -->
        <div
          v-if="message.message.type === MessageType.USER"
          class="d-flex justify-end mb-4 user-message"
        >
          <div class="ai-chat-message-bubble user-bubble body3">
            {{ message.message.text }}
          </div>
        </div>

        <!-- AI Response -->
        <div v-else-if="message.message.type === MessageType.ASSISTANT" class="ai-chat-response">
          <div class="d-flex mb-4-bubble ai-bubble">
            <!-- Events Summary Component -->
            <ai-assistant-events-summary-response
              v-if="message.message.content.type === ContentType.EVENTS_SUMMARY"
              :content="message.message.content"
              @learn-more="handleLearnMore"
            />

            <!-- Security Gaps Component -->
            <ai-assistant-security-gaps-summary-response
              v-else-if="message.message.content.type === ContentType.SECURITY_GAPS_SUMMARY"
              :content="message.message.content"
              :context="context"
              @action-click="handleSecurityGapActionClick"
            />

            <!-- Tool Approval Cards -->
            <ai-assistant-tool-approval-response
              v-else-if="message.message.content.type === ContentType.TOOL_APPROVAL_REQUIRED"
              :content="message.message.content"
              :context="context"
            />

            <!-- Standard Markdown & Docs Component -->
            <ai-assistant-markdown-response
              v-else
              :markdown-text="message.message.content.markdown"
              :doc-content="getDocumentationSearchContent(message)"
            />
          </div>

          <!--Hide feedback and suggestions for approval cards-->
          <template v-if="!isApprovalCardMessage(message)">
            <!-- Feedback component for AI response -->
            <ai-assistant-feedback
              class="mt-2"
              :feedback-rating="getFeedbackState(message.id)"
              @feedback-submitted="(rating) => submitFeedback(message.id, rating)"
            />

            <!-- 4. Suggestions Component (Only for last message) -->
            <template v-if="isLastAiMessage(message.id)">
              <ai-assistant-suggestions
                v-if="!hiddenSuggestions.includes(message.id)"
                :suggestions="getSuggestions(message)"
                :loading="loading"
                :refreshing="refreshingSuggestions"
                @refresh="refreshSuggestions"
                @suggestion-click="(text) => handleSuggestionClick(text, message.id)"
              />
            </template>
          </template>
        </div>
      </div>

      <!-- Loading State for New Message -->
      <div v-if="loading" class="mb-4">
        <!-- Analyzing Header -->
        <div class="mb-4 d-flex align-center">
          <coro-icon :icon-name="CoroIcons.AI_ASSISTANT_STAR" class="ai-assistant-star-icon mr-2" />
          <span class="body3">{{ $t("aiAssistant.chat.analyzing") }}</span>
        </div>

        <!-- Skeleton Loader -->
        <div class="d-flex flex-column ga-3">
          <div class="ai-skeleton-line long"></div>
          <div class="ai-skeleton-line long"></div>
          <div class="ai-skeleton-line long"></div>
          <div class="ai-skeleton-line short"></div>
        </div>
      </div>

      <!-- Spacer -->
      <div class="flex-grow-1"></div>
    </v-card-text>
  </div>
</template>

<script lang="ts">
import {
  type ComponentPublicInstance,
  computed,
  defineComponent,
  nextTick,
  onMounted,
  type PropType,
  ref,
  watch,
} from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import type {
  AiAssistantMessage,
  AiAssistantSuggestion,
  DocumentationSearchContent,
} from "@/_store/ai-assistant.module";
import { ContentType, MessageType, useAiAssistantStore } from "@/_store/ai-assistant.module";
import { stripMarkdown } from "@/_helpers/markdown";
import { CoroIcons } from "@/constants/coro-icon.ts";
import { FeedbackRating } from "@/constants/ai-assistant.ts";
import { AppContext } from "@/types.ts";
import CoroIcon from "@/components/CoroIcon.vue";
import AiAssistantEventsSummaryResponse from "./AiAssistantEventsSummaryResponse.vue";
import AiAssistantSecurityGapsSummaryResponse from "./AiAssistantSecurityGapsSummaryResponse.vue";
import AiAssistantMarkdownResponse from "./AiAssistantMarkdownResponse.vue";
import AiAssistantSuggestions from "./AiAssistantSuggestions.vue";
import AiAssistantFeedback from "./AiAssistantFeedback.vue";
import AiAssistantToolApprovalResponse from "@/components/ai-assistant/AiAssistantToolApprovalResponse.vue";

export default defineComponent({
  components: {
    AiAssistantToolApprovalResponse,
    CoroIcon,
    AiAssistantFeedback,
    AiAssistantEventsSummaryResponse,
    AiAssistantSecurityGapsSummaryResponse,
    AiAssistantMarkdownResponse,
    AiAssistantSuggestions,
  },
  props: {
    initialMessage: {
      type: [String, null] as PropType<string | null>,
      required: false,
      default: null,
    },
    context: {
      type: String as PropType<AppContext>,
      required: true,
    },
  },
  emits: ["back", "close"],
  setup(props) {
    const { t } = useI18n();
    const aiAssistantStore = useAiAssistantStore();
    const { messages, loading, refreshingSuggestions, suggestions } = storeToRefs(aiAssistantStore);
    const { refreshSuggestions } = aiAssistantStore;
    const chatContainer = ref<ComponentPublicInstance | null>(null);

    // Track which messages have had their suggestions clicked (hidden)
    const hiddenSuggestions = ref<string[]>([]);

    const chatMessages = computed(() => {
      return [...(messages.value || [])];
    });

    const getDocumentationSearchContent = (
      message: AiAssistantMessage
    ): DocumentationSearchContent | null => {
      if (
        message.message.type === MessageType.ASSISTANT &&
        message.message.content.type === ContentType.DOCUMENTATION_SEARCH
      ) {
        return message.message.content as DocumentationSearchContent;
      }
      return null;
    };

    const isApprovalCardMessage = (message: AiAssistantMessage) => {
      return (
        message.message.type === MessageType.ASSISTANT &&
        message.message.content.type === ContentType.TOOL_APPROVAL_REQUIRED
      );
    };

    const isLastAiMessage = (messageId: string) => {
      const allMessages = chatMessages.value;
      const aiMessages = allMessages.filter((msg) => msg.message.type === MessageType.ASSISTANT);
      const lastAiMessage = aiMessages[aiMessages.length - 1];
      return lastAiMessage?.id === messageId;
    };

    const getSuggestions = (message: AiAssistantMessage) => {
      let messageSuggestions: AiAssistantSuggestion[] = [];

      if (isLastAiMessage(message.id) && suggestions.value && suggestions.value.length > 0) {
        messageSuggestions = suggestions.value;
      } else if (
        message.message.type === MessageType.ASSISTANT &&
        message.message.suggestions &&
        message.message.suggestions.length > 0
      ) {
        messageSuggestions = message.message.suggestions;
      }

      return messageSuggestions.slice(0, 5);
    };

    const getFeedbackState = (messageId: string) => {
      const message = messages.value.find((msg) => msg.id === messageId);
      return message?.feedbackRating || null;
    };

    // Auto-send initial message when component mounts (if not already in chat history)
    onMounted(async () => {
      if (props.initialMessage) {
        // Check if this message is already in the chat history
        const existingMessage = messages.value.find(
          (msg) =>
            msg.message.type === MessageType.USER && msg.message.text === props.initialMessage
        );

        // Only send if not already in history
        if (!existingMessage) {
          await aiAssistantStore.sendMessage({
            message: props.initialMessage,
          });
        }
      }
    });

    const submitFeedback = async (messageId: string, rating: FeedbackRating) => {
      await aiAssistantStore.submitFeedback(messageId, { rating });
    };

    const handleLearnMore = async (content: string) => {
      const plainTextContent = stripMarkdown(content);
      const message = t("aiAssistant.learnMore.prompt", { content: plainTextContent });

      await aiAssistantStore.sendMessage({ message });
    };

    const handleSuggestionClick = async (suggestion: string, messageId: string) => {
      // Hide suggestions for this message
      if (!hiddenSuggestions.value.includes(messageId)) {
        hiddenSuggestions.value.push(messageId);
      }

      await aiAssistantStore.sendMessage({ message: suggestion });
    };

    const handleSecurityGapActionClick = async (message: string) => {
      await aiAssistantStore.sendMessage({ message });
    };

    // Wait for DOM updates so scrollHeight reflects newly rendered messages.
    const scrollToBottom = async () => {
      await nextTick();
      const target = chatContainer.value?.$el as HTMLElement | undefined;
      if (!target) return;
      target.scrollTo({
        top: target.scrollHeight,
        behavior: "smooth",
      });
    };

    watch(
      () => messages.value.length,
      (newLength, oldLength) => {
        if (newLength > oldLength) {
          const lastMessage = messages.value.at(-1);
          if (lastMessage?.message.type === MessageType.USER) {
            // Keep the latest user message visible when a new user message is appended.
            scrollToBottom();
          }
        }
      }
    );

    return {
      loading,
      chatMessages,
      getDocumentationSearchContent,
      getSuggestions,
      isLastAiMessage,
      getFeedbackState,
      submitFeedback,
      handleLearnMore,
      handleSuggestionClick,
      CoroIcons,
      FeedbackRating,
      MessageType,
      ContentType,
      hiddenSuggestions,
      refreshSuggestions,
      refreshingSuggestions,
      isApprovalCardMessage,
      handleSecurityGapActionClick,
      chatContainer,
    };
  },
});
</script>

<style lang="scss" scoped>
.ai-chat-content {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

.ai-chat-message-bubble {
  max-width: 80%;
  padding: 12px 16px;
  border-radius: 16px;

  &.user-bubble {
    background-color: rgb(var(--v-theme-indigo-pale));
    border-bottom-right-radius: 4px;
  }

  &.ai-bubble {
    background-color: rgb(var(--v-theme-gray-light));
    color: rgb(var(--v-theme-gray-dark));
    border-bottom-left-radius: 4px;
  }
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.ai-assistant-star-icon {
  width: 16px;
  height: 16px;
}

.ai-skeleton-line {
  height: 24px;
  background: linear-gradient(90deg, rgba(205, 141, 229, 0.25) 0%, rgba(22, 164, 246, 0.25) 100%);
  background-size: 200% 100%;
  border-radius: 8px;
  animation: shimmer 1.5s infinite;

  &.long {
    width: 85%;
  }

  &.short {
    width: 45%;
  }
}
</style>
